/**
 * Hazards of the ground itself: mines, barbed wire, water and ice, proximity posts (02 §4,
 * §9.2). This module owns `world.hazards` and everything in `HazardState`. One file each:
 *
 *   terrain.ts  what the ground is (water, ice, cracked ice, fords) and the nav grid's share of it
 *   mines.ts    mines under the ranger and the minitank; proximity posts, tripped or shot
 *   blast.ts    what any explosion does here: called from `damage.ts::explode`
 *   wire.ts     forcing a way through barbed wire
 *   water.ts    hiding under water, drowning, and ice that gives way
 *   searchlights.ts  the sweeping beams of a night garrison, and who is caught in them
 *   spans.ts    a bridge whose every pier has been blown comes down with them
 *   tracks.ts   footprints in snow and sand, and the searching soldier who follows them
 *
 * Mines are laid here, deterministically, from the mission's minefield rectangles.
 * `Mine.live` is the truth the renderer's Mine Sense overlay reads.
 */

import { type Ctx, isAlive } from '../ctx';
import { stepSpecialsUpkeep } from '../enemies/specials';
import { Pcg32 } from '../rng';
import { resolveMovement } from '../statics';
import { RANGER, SPECIALS } from '../tuning';
import type { HazardState, Mine, MissionDef } from '../types';
import { stepMines, stepProximityPosts } from './mines';
import { createSearchlights, stepSearchlights } from './searchlights';
import { stepSpans } from './spans';
import { stepTracks } from './tracks';
import { stepIce, stepWater } from './water';
import { stepWire } from './wire';

export { rangerLit, rangerSubmerged } from './terrain';

export function createHazardState(def: MissionDef): HazardState {
  const rng = new Pcg32(def.seed, 31);
  const mines: Mine[] = [];
  let id = 1;
  for (const field of def.mineFields) {
    const w = field.rect.x1 - field.rect.x0;
    const d = field.rect.z1 - field.rect.z0;
    const count = Math.max(1, Math.round((w * d * field.density) / 100));
    for (let i = 0; i < count; i++) {
      const x = field.rect.x0 + rng.float() * w;
      mines.push({ id: id++, fieldId: field.id, x, z: field.rect.z0 + rng.float() * d, live: true });
    }
  }
  return {
    mines, wireTicks: 0, wirePushTicks: 0, wireExit: null, submergedTicks: 0, iceHeld: false,
    enemyRockets: [], fuelTanks: [], searchlights: createSearchlights(def), litTicks: 0, slideX: 0, slideZ: 0,
    tracks: [],
  };
}

/** A mini-bunker is an `Enemy`, so it has no collider: keep the ranger out of its concrete by hand. */
function keepOutOfBunkers(ctx: Ctx): void {
  const r = ctx.world.ranger;
  const min = SPECIALS.bunkerBody + RANGER.radius;
  for (const e of ctx.world.enemies) {
    if (e.kind !== 'miniBunker' || !isAlive(e)) continue;
    const dx = r.x - e.x;
    const dz = r.z - e.z;
    const d2 = dx * dx + dz * dz;
    if (d2 >= min * min) continue;
    const d = Math.sqrt(d2);
    // Straight back out the way he came in (due south if he is somehow dead centre).
    const ux = d < 1e-6 ? 0 : dx / d;
    const uz = d < 1e-6 ? 1 : dz / d;
    const out = resolveMovement(ctx.statics, e.x + ux * min, e.z + uz * min, RANGER.radius);
    r.x = out.x;
    r.z = out.z;
  }
}

/** Runs on the ground, right after the ranger's own step and before the garrison's. */
export function stepHazards(ctx: Ctx): void {
  stepWire(ctx);
  keepOutOfBunkers(ctx);
  stepWater(ctx);
  stepIce(ctx);
  // After the water, so a step taken into a river has already wiped the back-trail it breaks.
  stepTracks(ctx);
  stepMines(ctx);
  stepProximityPosts(ctx);
  stepSearchlights(ctx);
  stepSpecialsUpkeep(ctx);
  stepSpans(ctx);
}
